import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

interface TenantMemberOption {
  userId: string;
  userName: string;
  userEmail: string;
}

interface Props {
  projectId: string;
  tenantId: string;
  role: string;
  roleLabel: string;
  /** userIds who already hold this role (as primary or delegate) — excluded from the picker. */
  excludeUserIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddDelegateModal({ projectId, tenantId, role, roleLabel, excludeUserIds, onClose, onSuccess }: Props) {
  const [options, setOptions] = useState<TenantMemberOption[] | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loadError, setLoadError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);
  const excluded = useRef(new Set(excludeUserIds)).current;

  useEffect(() => {
    apiFetch<TenantMemberOption[]>(`/tenants/${tenantId}/members`)
      .then(data => setOptions(data.filter(m => !excluded.has(m.userId))))
      .catch(err => setLoadError(err instanceof Error ? err.message : 'Failed to load tenant members'));
  }, [tenantId, excluded]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      await apiFetch(`/projects/${projectId}/delegates`, {
        method: 'POST',
        body: JSON.stringify({ userId: selectedUserId, role }),
      });
      setSuccess(true);
      setTimeout(() => { onSuccess(); onClose(); }, 1000);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to add delegate');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border)',
    background: 'var(--surface-alt)',
    color: 'var(--text)',
    fontFamily: 'var(--font-body)',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%', maxWidth: 480,
          maxHeight: '85vh',
          display: 'flex', flexDirection: 'column',
          background: 'var(--surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {success ? (
          <div style={{ padding: 'var(--space-6)' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Add delegate</h2>
            <div
              style={{
                marginTop: 16,
                padding: '12px 16px',
                background: 'rgba(16,185,129,0.08)',
                border: '1px solid rgba(16,185,129,0.3)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)',
                fontSize: 14,
                color: 'var(--success)',
              }}
            >
              ✓ Delegate added!
            </div>
          </div>
        ) : (
          <form onSubmit={e => void handleSubmit(e)} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: 'var(--space-6)', paddingBottom: 0 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Add delegate</h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 20 }}>
                Adding a delegate for: <strong style={{ color: 'var(--text)' }}>{roleLabel}</strong>. A delegate can act on
                this role&rsquo;s behalf, with the primary notified and able to revoke within the project&rsquo;s revocation window.
              </p>

              <div style={{ marginBottom: 16 }}>
                <label
                  style={{
                    display: 'block', fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                    color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em',
                  }}
                >
                  Tenant member
                </label>
                {loadError ? (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--error)' }}>{loadError}</p>
                ) : options === null ? (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p>
                ) : options.length === 0 ? (
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)' }}>
                    No other tenant members are available to add as a delegate for this role.
                  </p>
                ) : (
                  <select
                    value={selectedUserId}
                    onChange={e => setSelectedUserId(e.target.value)}
                    disabled={submitting}
                    required
                    style={inputStyle}
                  >
                    <option value="" disabled>Select a person…</option>
                    {options.map(o => (
                      <option key={o.userId} value={o.userId}>{o.userName} · {o.userEmail}</option>
                    ))}
                  </select>
                )}
              </div>

              {submitError && (
                <div
                  style={{
                    marginBottom: 16, padding: '10px 12px',
                    background: 'rgba(239,68,68,0.06)',
                    border: '1px solid rgba(239,68,68,0.2)',
                    borderRadius: 'var(--radius-sm)',
                    fontFamily: 'var(--font-body)', fontSize: 13,
                    color: 'var(--error)',
                  }}
                >
                  {submitError}
                </div>
              )}
            </div>

            <div style={{ flexShrink: 0, display: 'flex', gap: 10, justifyContent: 'flex-end', padding: '16px var(--space-6) var(--space-6)', borderTop: '1px solid var(--border)' }}>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 500,
                  padding: '9px 18px', borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border)', background: 'transparent',
                  color: 'var(--text-secondary)', cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !selectedUserId}
                style={{
                  fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600,
                  padding: '9px 18px', borderRadius: 'var(--radius-sm)',
                  border: 'none', background: 'var(--primary)', color: '#fff',
                  cursor: submitting || !selectedUserId ? 'not-allowed' : 'pointer',
                  opacity: submitting || !selectedUserId ? 0.5 : 1,
                }}
              >
                {submitting ? 'Adding…' : 'Add Delegate'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
